import { makeStyles } from 'tss-react/mui';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { CartItem } from '../../../utils/cart';
import {
  checkForUnavailableRequiredModifierGroup,
  checkItemInCartItemTree,
  isItem86edToday,
  splitPathToItem,
} from '../../../utils/menu';
import MultipleSelectionModGroup from './MultipleSelectionModGroup';
import SingularSelectionModGroup from './SingularSelectionModGroup';
import Delayed from './Delayed';
import { ParsedMenuItem, ParsedModifierGroup } from '../../../types/menu';
import { getMenuUnificationFeatureFlag } from '../../../utils/session-storage';
import { useSelector } from 'react-redux';
import { selectDialogHighlightedModifier } from '../../../selectors/dialog';
import Colors from '../../../utils/color';
import logger from '../../../utils/logger';
import { isBMModifierAnUnavailableConstraint } from '../../../utils/menuUnification.utils';
import { selectUnavailableItemsByName } from '../../../selectors/menu';
import useTTSMessage from '../../../hooks/useTTSMessage.hooks';

interface OwnProps {
  modGroup: ParsedModifierGroup;
  cartItem: CartItem;
  selectMenuItemModifier: (
    menuItem: ParsedMenuItem,
    modGroup: ParsedModifierGroup,
    pathToItem: string,
    selected: boolean,
    fromClick?: boolean,
    isDefaultSelection?: boolean
  ) => void;
  pathToItem: string;
  isSelectedToFocus?: boolean; // Selected by Tab key
  isHighlighted?: boolean; // Highlighted in Omnibar search result
}

export interface IModGroupStyleProps {
  isSelectedToFocus?: boolean;
}

const useStyles = makeStyles<IModGroupStyleProps>()(
  ({ spacing }, { isSelectedToFocus }) => {
    const bgColor = isSelectedToFocus ? Colors.alto : Colors.white;
    return {
      modGroupClass: {
        marginLeft: spacing(1.5),
        backgroundColor: bgColor,
      },
    };
  }
);

const ModGroup = (props: OwnProps) => {
  const modGroupRef = useRef<HTMLDivElement>(null);
  const {
    modGroup,
    cartItem,
    selectMenuItemModifier,
    pathToItem,
    isSelectedToFocus,
  } = props;

  const isBrandMenu = getMenuUnificationFeatureFlag();
  const highlightedModifier = useSelector(selectDialogHighlightedModifier);
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);
  const { sendModGroupTTSMessage, sendTTSMessage } = useTTSMessage();

  const isHighlighted = highlightedModifier === pathToItem;
  let parentModifier = '';
  const nodesInPath = splitPathToItem(pathToItem);
  if (nodesInPath.length > 2) {
    parentModifier = nodesInPath[nodesInPath.length - 2];
  }

  const { classes } = useStyles({
    isSelectedToFocus,
  });

  useEffect(() => {
    if (isSelectedToFocus || isHighlighted) {
      modGroupRef.current?.scrollIntoView();
    }
  }, [isSelectedToFocus, isHighlighted]);

  const allModifierUnavailable = !Object.values(modGroup.menuItems).find(
    (menuItem) => {
      if (
        (menuItem.available || isItem86edToday(menuItem)) &&
        !checkForUnavailableRequiredModifierGroup(menuItem.modifierGroups)
      ) {
        return menuItem;
      }
    }
  );
  const cartModGroup = checkItemInCartItemTree({ cartItem, pathToItem });

  useEffect(() => {
    // handle default modifier only when it doesn't exit in the cartModGroup
    if (!cartModGroup) {
      modGroup.defaultSelectedItemIds?.forEach((itemId: string | number) => {
        if (
          isBrandMenu &&
          isBMModifierAnUnavailableConstraint(
            cartItem,
            unavailableItemsByName,
            parentModifier,
            pathToItem,
            modGroup.name,
            String(itemId)
          )
        ) {
          // Skip: this brand-menu default item is an unavailable constraint.
        } else {
          const matchingItems = Object.values(modGroup.menuItems).filter(
            (item) => item.itemId === String(itemId)
          );

          if (!matchingItems.length) {
            logger.warn({
              message: `Warning: Default item ${itemId} not found in modifier group ${modGroup.id} for cart item ${cartItem.name}`,
            });
          } else if (matchingItems.length > 1) {
            logger.warn({
              message: `Warning: Multiple items found with itemId ${itemId} in modifier group ${modGroup.id} for cart item ${cartItem.name}`,
            });
          }

          matchingItems.forEach((item) =>
            selectMenuItemModifier(
              item,
              modGroup,
              pathToItem,
              true,
              false,
              true
            )
          );
        }
      });
    }
  }, [cartModGroup, pathToItem]);

  const isSingleChoice =
    modGroup.minimumSelections === modGroup.maximumSelections &&
    modGroup.maximumSelections === 1;

  const handleModChange = useCallback(
    (innerModGroup: ParsedModifierGroup) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const itemId = event.target.value;
        const isChecked = isSingleChoice || event.target.checked;
        if (itemId.length > 0) {
          selectMenuItemModifier(
            innerModGroup.menuItems[itemId],
            innerModGroup,
            pathToItem,
            isChecked,
            true,
            false
          );
        }
      },
    [isSingleChoice, pathToItem, selectMenuItemModifier]
  );

  const modGroupProps = {
    cartItem,
    key: `${modGroup.id}_main_node`,
    modGroup,
    cartModGroup,
    onChange: handleModChange,
    selectMenuItemModifier,
    isSelectedToFocus,
    isHighlighted,
    parentModifier: isBrandMenu ? parentModifier : '',
    sendModGroupTTSMessage,
    sendTTSMessage,
  };

  const content = (
    <div
      data-testid="mod-group"
      className={classes.modGroupClass}
      ref={modGroupRef}
    >
      {allModifierUnavailable ? null : (
        <>
          {isSingleChoice ? (
            <SingularSelectionModGroup
              {...modGroupProps}
              pathToItem={pathToItem}
            />
          ) : (
            <MultipleSelectionModGroup
              {...modGroupProps}
              pathToItem={pathToItem}
            />
          )}
        </>
      )}
    </div>
  );

  // The Delayed reveal exists to keep the item's initial mount from blocking
  // first paint (HITL-720). Nested groups mount one at a time when the agent
  // selects their parent option — small, render-scoped work — so the fixed
  // delay there is pure latency on every nested expand (HITL-1422). Top-level
  // groups (single-segment path) keep the deferred reveal.
  const isNestedGroup = nodesInPath.length > 1;
  return isNestedGroup ? content : <Delayed>{content}</Delayed>;
};

// Re-render a mod group only when ITS slice of the cart changed. The cart
// item object gets a new identity on every cart edit, which would otherwise
// re-render every mounted group (a crowd pleaser mounts dozens) on each
// click. Immer's copy-on-write guarantees the node returned by
// checkItemInCartItemTree changes reference iff something inside this
// group's subtree was modified, so comparing node references is exact
// (HITL-1422).
const areModGroupPropsEqual = (prev: OwnProps, next: OwnProps): boolean => {
  if (
    prev.modGroup !== next.modGroup ||
    prev.pathToItem !== next.pathToItem ||
    prev.selectMenuItemModifier !== next.selectMenuItemModifier ||
    prev.isSelectedToFocus !== next.isSelectedToFocus ||
    prev.isHighlighted !== next.isHighlighted
  ) {
    return false;
  }
  if (prev.cartItem === next.cartItem) {
    return true;
  }
  if (prev.cartItem.cartItemId !== next.cartItem.cartItemId) {
    return false;
  }
  // Normalize the miss value: the tree walk returns null when the cart has no
  // child groups at all yet, but undefined once any group exists — both mean
  // "this group has no selections", and treating them as different would
  // re-render every group on the first selection.
  const prevNode =
    checkItemInCartItemTree({
      cartItem: prev.cartItem,
      pathToItem: prev.pathToItem,
    }) ?? null;
  const nextNode =
    checkItemInCartItemTree({
      cartItem: next.cartItem,
      pathToItem: next.pathToItem,
    }) ?? null;
  return prevNode === nextNode;
};

export default memo(ModGroup, areModGroupPropsEqual);
